import {EntityList, EntityListColumn} from 'components';
import {LoadWrapper} from 'components/LoadWrapper';
import {ViewAuthorizer} from 'components/ViewAuthorizer';
import React, {useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {ReportEventType, User, UserRole} from 'types';
import {NotificationTooltipTranslationProvider} from 'features/localizedTooltips';
import {HeaderForPagesWithEntityPage} from 'components/HeaderForPagesWithEntityPage';
import {renderArrayField} from 'components/EntityList/ColumnRenderers';
import {useEditEntityCmd} from 'hooks/useEditEntityCmd';
import {useSelection} from 'hooks';
import AdminNotificatonEditKeywordForm, {
	NotificationType,
} from './AdminNotificatonEditKeywordForm';
import {
	GetAllKeywordVexExportConfigUsersDocument,
	useAssignReportConfigurationMutation,
	useGetAllKeywordVexExportConfigUsersQuery,
} from './hooks/assignReportConfig.generated';

const AdminNotificationPage: React.FC = () => {
	const {t} = useTranslation('features/adminsection', {
		keyPrefix: 'AdminNotifications',
	});

	const {loading, data} = useGetAllKeywordVexExportConfigUsersQuery();
	const [assignReportConfiguration] = useAssignReportConfigurationMutation();

	const reportConfigUsers: NotificationType[] = useMemo(() => {
		const keywordNotification: NotificationType = {
			id: 'keyword-monthly',
			name: t('KeywordMonthly'),
			users: data?.allKeywordVexExportConfigUsers ?? [],
		};

		return [keywordNotification];
	}, [data, t]);

	const columns: EntityListColumn[] = useMemo(
		() => [
			{
				key: 'name',
				fieldName: 'name',
				name: t('Notification'),
				minWidth: 200,
				targetWidthProportion: 1,
			},
			{
				key: 'users',
				fieldName: 'users',
				name: t('Users'),
				minWidth: 600,
				targetWidthProportion: 3,
				onRender: renderArrayField(),
			},
		],
		[t],
	);

	const [selection] = useSelection<NotificationType>({
		getKey: item => item.id,
	});

	const [editFormOpen, setEditFormOpen] = useState(false);
	const [selectedNotification, setSelectedNotification] =
		useState<NotificationType | null>(null);

	useEditEntityCmd(
		{
			key: 'edit',
			onClick() {
				const selected = selection?.getSelection()[0] as NotificationType;
				setSelectedNotification(selected);
				setEditFormOpen(true);
			},
			disabled: selection?.count === 0,
		},
		[selection?.count],
	);

	const onKeywordFormSave = (newUsers: User[]) => {
		if (newUsers.length > 0 && selectedNotification) {
			const previousUsers = data?.allKeywordVexExportConfigUsers ?? [];
			const newUserIds = newUsers.map(u => u.id);
			const previousUserIds = previousUsers.map(u => u.id);

			const addedUsers = newUserIds.filter(id => !previousUserIds.includes(id));
			const removedUsers = previousUserIds.filter(
				id => !newUserIds.includes(id),
			);
			assignReportConfiguration({
				variables: {
					input: {
						reportEventType: ReportEventType.KeywordVexExport,
						addedUsers,
						removedUsers,
					},
				},
				refetchQueries: [GetAllKeywordVexExportConfigUsersDocument],
			});
		}
	};

	return (
		<ViewAuthorizer roles={[UserRole.SystemAdministrator]}>
			<HeaderForPagesWithEntityPage>
				<h3>{t('Notifications')}</h3>
			</HeaderForPagesWithEntityPage>

			<AdminNotificatonEditKeywordForm
				data={selectedNotification ? [selectedNotification] : []}
				isOpen={editFormOpen}
				onClose={() => setEditFormOpen(false)}
				onConfirm={onKeywordFormSave}
			/>

			<LoadWrapper loading={loading}>
				<NotificationTooltipTranslationProvider>
					<EntityList
						items={reportConfigUsers}
						columns={columns}
						selection={selection}
						hideExcelButton={true}
					/>
				</NotificationTooltipTranslationProvider>
			</LoadWrapper>
		</ViewAuthorizer>
	);
};

export default AdminNotificationPage;
